// Flight plan layer family — plan route line + waypoint pins.
//
// One GeoJSON source (`plan`) drives 13 visual layers:
//   - Enroute legs: halo (plan-line-glow) + border (plan-line-border) +
//     main (plan-line). Color-coded by legState: orange when active/flown,
//     blue otherwise.
//   - IAP/procedure legs: halo + border + main, scaled thinner to read as
//     subordinate to the enroute line (matches FAA chart convention).
//     Orange for active/flown, magenta otherwise.
//   - Missed-approach legs: faint halo + dashed magenta main, no border.
//     Always magenta — never "flown" in the legState sense.
//   - Synthetic legs (altitude-terminator projections): sparser dash,
//     slight transparency signals 'approximate'. Orange active/flown, magenta otherwise.
//   - Point symbols: colored circle dots (plan-points), procedure-role
//     icons (plan-role-icons: FAF Maltese cross / IAF circle / MAP square).
//   - Labels: ident labels at top of every fix (plan-labels), altitude-
//     constraint labels right of fix when altLabel is set (plan-alt-labels).
//
// NOTE: id suffix '-mount' to avoid colliding with any future
// registerStyleRebuild that might own apply for this family. The
// data is pushed by pushPlan(map) called from MapView's styledata
// handler. See src/map/layers/terrain.ts for the canonical pattern.
// Extracted from addOverlays.ts as HI-1 batch 8 — final batch (2026-05-24).
// Pure mechanical move — source spec, paint expressions, filter expressions,
// layer ids, minzoom, and layer order all byte-identical to the pre-extraction form.
// Color constants BLUE_MAIN, BLUE_BORDER, ORANGE_MAIN, ORANGE_BORDER,
// MAGENTA_MAIN, MAGENTA_BORDER moved here from addOverlays.ts (were only
// used in this block).

import type { Map as MlMap } from 'maplibre-gl';
import { EMPTY_FC } from '../MapView';
import { registerLayerFamily } from '../layerVisibilityRegistry';
import { useLayerVisibilityStore } from '../../stores/layerVisibilityStore';

// Route-line color tokens (standardized ForeFlight-style scheme).
// ONE orange across enroute + IAP for active+flown ("where you ARE
// / where you HAVE BEEN") — single color simplifies the read. Each
// BORDER value is a darker/desaturated sibling of MAIN, used in the
// middle of the halo→border→main stack to give the line a crisp
// edge against terrain and sectional basemaps.
const BLUE_MAIN      = '#38bdf8';
const BLUE_BORDER    = '#0284c7';
const ORANGE_MAIN    = '#fb923c';
const ORANGE_BORDER  = '#c2410c';
const MAGENTA_MAIN   = '#e040fb';
const MAGENTA_BORDER = '#a21caf';

/**
 * Mount the flight-plan source and all derived layers.
 * Idempotent on the source.
 */
export function addPlanLayers(map: MlMap): void {
  // NOTE: We use modern expression-style geometry-type filters
  // (['==', ['geometry-type'], 'LineString']) instead of the legacy
  // '$type' syntax. The legacy form is documented as deprecated but
  // still working — except in MapLibre 4.7.1 we observed it silently
  // not rendering ANY pixels for the plan-line + plan-line-glow layers
  // (queryRenderedFeatures returned 0 across a 20x20 sample grid even
  // though the LineString feature was correctly in the source). Modern
  // expression filters work as expected.
  if (!map.getSource('plan')) {
    map.addSource('plan', { type: 'geojson', data: EMPTY_FC });

    // Enroute legs: halo + border + main (12 / 8 / 5 px).
    map.addLayer({
      id: 'plan-line-glow',
      type: 'line',
      source: 'plan',
      filter: ['all',
        ['==', ['geometry-type'], 'LineString'],
        ['==', ['get', 'kind'], 'enroute'],
      ],
      paint: {
        'line-color': [
          'match', ['get', 'legState'],
          'active', ORANGE_MAIN,
          'flown',  ORANGE_MAIN,
          BLUE_MAIN,
        ],
        'line-width': 12,
        'line-opacity': 0.18,
        'line-blur': 2,
      },
    });
    map.addLayer({
      id: 'plan-line-border',
      type: 'line',
      source: 'plan',
      filter: ['all',
        ['==', ['geometry-type'], 'LineString'],
        ['==', ['get', 'kind'], 'enroute'],
      ],
      paint: {
        'line-color': [
          'match', ['get', 'legState'],
          'active', ORANGE_BORDER,
          'flown',  ORANGE_BORDER,
          BLUE_BORDER,
        ],
        'line-width': 8,
        'line-opacity': 0.65,
      },
    });
    map.addLayer({
      id: 'plan-line',
      type: 'line',
      source: 'plan',
      filter: ['all',
        ['==', ['geometry-type'], 'LineString'],
        ['==', ['get', 'kind'], 'enroute'],
      ],
      paint: {
        'line-color': [
          'match', ['get', 'legState'],
          'active', ORANGE_MAIN,
          'flown',  ORANGE_MAIN,
          BLUE_MAIN,
        ],
        'line-width': 5,
      },
    });
    // IAP/procedure legs: halo + border + main, scaled to 10 / 7 / 4 px
    // so they read as visually subordinate to the enroute line (matches
    // FAA chart convention — IAPs are drawn thinner than the enroute
    // route line on sectionals and IFR enroute charts).
    map.addLayer({
      id: 'plan-line-procedure-glow',
      type: 'line',
      source: 'plan',
      filter: ['all',
        ['==', ['geometry-type'], 'LineString'],
        ['==', ['get', 'kind'], 'procedure'],
      ],
      paint: {
        'line-color': [
          'match', ['get', 'legState'],
          'active', ORANGE_MAIN,
          'flown',  ORANGE_MAIN,
          MAGENTA_MAIN,
        ],
        'line-width': 10,
        'line-opacity': 0.18,
        'line-blur': 2,
      },
    });
    map.addLayer({
      id: 'plan-line-procedure-border',
      type: 'line',
      source: 'plan',
      filter: ['all',
        ['==', ['geometry-type'], 'LineString'],
        ['==', ['get', 'kind'], 'procedure'],
      ],
      paint: {
        'line-color': [
          'match', ['get', 'legState'],
          'active', ORANGE_BORDER,
          'flown',  ORANGE_BORDER,
          MAGENTA_BORDER,
        ],
        'line-width': 7,
        'line-opacity': 0.65,
      },
    });
    map.addLayer({
      id: 'plan-line-procedure',
      type: 'line',
      source: 'plan',
      filter: ['all',
        ['==', ['geometry-type'], 'LineString'],
        ['==', ['get', 'kind'], 'procedure'],
      ],
      paint: {
        'line-color': [
          'match', ['get', 'legState'],
          'active', ORANGE_MAIN,
          'flown',  ORANGE_MAIN,
          MAGENTA_MAIN,
        ],
        'line-width': 4,
      },
    });
    // Missed-approach: faint halo + dashed magenta main, no border.
    // Always magenta — boss spec: it's a future-only contingency, never
    // "flown" in the legState sense, so no need to recolor. Skipping
    // the border layer keeps it visually quieter than the live route.
    map.addLayer({
      id: 'plan-line-procedure-missed-glow',
      type: 'line',
      source: 'plan',
      filter: ['all',
        ['==', ['geometry-type'], 'LineString'],
        ['==', ['get', 'kind'], 'procedure-missed'],
      ],
      paint: {
        'line-color': MAGENTA_MAIN,
        'line-width': 8,
        'line-opacity': 0.15,
        'line-blur': 2,
      },
    });
    map.addLayer({
      id: 'plan-line-procedure-missed',
      type: 'line',
      source: 'plan',
      filter: ['all',
        ['==', ['geometry-type'], 'LineString'],
        ['==', ['get', 'kind'], 'procedure-missed'],
      ],
      paint: {
        'line-color': MAGENTA_MAIN,
        'line-width': 4,
        'line-dasharray': [3, 2],
      },
    });
    // Synthetic legs (altitude-terminator projections). Sparser dash +
    // slight transparency signals 'approximate'. Adopts the standardized
    // scheme: orange when active/flown, magenta otherwise.
    map.addLayer({
      id: 'plan-line-procedure-synthetic',
      type: 'line',
      source: 'plan',
      filter: ['all',
        ['==', ['geometry-type'], 'LineString'],
        ['==', ['get', 'kind'], 'procedure-synthetic'],
      ],
      paint: {
        'line-color': [
          'match', ['get', 'legState'],
          'active', ORANGE_MAIN,
          'flown',  ORANGE_MAIN,
          MAGENTA_MAIN,
        ],
        'line-width': 2.5,
        'line-dasharray': [1, 2],
        'line-opacity': 0.7,
      },
    });
    map.addLayer({
      id: 'plan-points',
      type: 'circle',
      source: 'plan',
      filter: ['==', ['geometry-type'], 'Point'],
      paint: {
        'circle-color': [
          'case',
          ['==', ['get', 'kind'], 'procedure-missed'],   '#e040fb',
          ['==', ['get', 'kind'], 'procedure-synthetic'],'#e040fb',
          ['==', ['get', 'kind'], 'procedure'],          '#e040fb',
          '#ff5a1f',
        ],
        'circle-radius': 6,
        'circle-stroke-color': '#0b0d0f',
        'circle-stroke-width': 2,
        'circle-opacity': [
          'case',
          ['==', ['get', 'kind'], 'procedure-synthetic'], 0.6,
          1,
        ],
      },
    });
    // Procedure role icons (FAF Maltese cross / IAF circle / MAP square).
    // Sits between the dot layer and the ident-label layer.
    map.addLayer({
      id: 'plan-role-icons',
      type: 'symbol',
      source: 'plan',
      filter: ['all',
        ['==', ['geometry-type'], 'Point'],
        ['has', 'procRole'],
        ['!=', ['get', 'procRole'], null],
      ],
      layout: {
        'icon-image': [
          'match', ['get', 'procRole'],
          'faf', 'proc-maltese-cross',
          'iaf', 'proc-iaf-circle',
          'map', 'proc-map-square',
          'proc-maltese-cross',
        ],
        'icon-size': 0.7,
        'icon-allow-overlap': true,
        'icon-anchor': 'center',
      },
    });

    // Ident labels (top of every fix).
    map.addLayer({
      id: 'plan-labels',
      type: 'symbol',
      source: 'plan',
      filter: ['==', ['geometry-type'], 'Point'],
      layout: {
        'text-field': ['get', 'ident'],
        'text-size': 11,
        'text-offset': [0, -1.4],
        'text-anchor': 'bottom',
        'text-font': ['Noto Sans Regular'],
      },
      paint: {
        'text-color': '#ffd2b3',
        'text-halo-color': '#0b0d0f',
        'text-halo-width': 1.5,
      },
    });

    // Altitude-constraint labels (bottom of fix when altLabel is set).
    // Right-side anchored so they don't overlap the ident at the top.
    map.addLayer({
      id: 'plan-alt-labels',
      type: 'symbol',
      source: 'plan',
      filter: ['all',
        ['==', ['geometry-type'], 'Point'],
        ['has', 'altLabel'],
        ['!=', ['get', 'altLabel'], null],
      ],
      layout: {
        'text-field': ['get', 'altLabel'],
        'text-size': 10,
        'text-offset': [1.4, 0],
        'text-anchor': 'left',
        'text-font': ['Noto Sans Regular'],
        'text-line-height': 1.0,
      },
      paint: {
        'text-color': '#ffb0f5',
        'text-halo-color': '#0b0d0f',
        'text-halo-width': 1.8,
      },
    });
  }
}

// NOTE: id suffix '-mount' to avoid colliding with any future
// registerStyleRebuild that might own apply for this family. The
// data is pushed by pushPlan(map) called from MapView's styledata
// handler. See src/map/layers/terrain.ts for the canonical pattern.
const PLAN_VISIBILITY_LAYER_IDS = [
  'plan-line-glow', 'plan-line-border', 'plan-line',
  'plan-line-procedure-glow', 'plan-line-procedure-border', 'plan-line-procedure',
  'plan-line-procedure-missed-glow', 'plan-line-procedure-missed',
  'plan-line-procedure-synthetic',
  'plan-points', 'plan-role-icons', 'plan-labels', 'plan-alt-labels',
] as const;

registerLayerFamily({
  id: 'plan-mount',
  label: 'Flight plan layers',
  sourceId: 'plan',
  layerIds: [...PLAN_VISIBILITY_LAYER_IDS],
  mount: (map) => addPlanLayers(map),
  visibilitySelector: (l) => l.flightPlan,
  applyState: (map) => {
    const show = useLayerVisibilityStore.getState().layers.flightPlan;
    const v = show ? 'visible' : 'none';
    for (const id of PLAN_VISIBILITY_LAYER_IDS) {
      if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', v);
    }
  },
});
